import React from 'react'
import dayjs from 'dayjs'
import { Product, ItemAvailability } from 'schema-dts'
import {
  getImagesFromVariantOrPlaceholder,
  IProduct,
  IVariant,
} from '@core/api/Products/types'
import config from '@core/config/config'
import { formatPrice } from '@core/utils/i18n'
import { useServices } from '@core/utils/ioc'
import { JsonLd } from '@core/utils/jsonld'
import getSku from '@core/utils/models/getSku'

interface IProps {
  product: IProduct
  variant: IVariant
}

const LYSProductJsonLd: React.FC<IProps> = ({ product, variant }) => {
  const { router, productContextService } = useServices()
  const { price } = variant
  const productContext = productContextService.getProductDetailContext(
    product,
    variant
  )
  const outOfStock = productContext.stock.isOutOfStock

  const images = getImagesFromVariantOrPlaceholder(variant)

  const productPrice = price && price.gross

  if (!productPrice) {
    return null
  }

  const formattedPrice = `${formatPrice(productPrice, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  })}`.replace(',', '.')

  const url = config.baseUrl + router.asPath

  const endOfNextYear = dayjs().endOf('year').add(1, 'year')
  const endOfNextYearFormatted = endOfNextYear.format('YYYY-MM-DD')

  return (
    <JsonLd<Product>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        image: images.map(({ url }) => url),
        name: product.name,
        sku: getSku(product, variant),
        gtin: variant.gtin,
        gtin13: variant.gtin,
        description: product.description,
        brand: {
          '@type': 'Brand',
          name: product.brand?.name || config.storeName,
        },

        offers: [
          {
            '@type': 'Offer',
            price: `${formattedPrice}`,
            priceCurrency: price?.currency || 'EUR',
            priceValidUntil: endOfNextYearFormatted,
            availability: outOfStock
              ? ('http://schema.org/OutOfStock' as ItemAvailability)
              : ('http://schema.org/InStock' as ItemAvailability),
            url,
            seller: {
              '@type': 'Organization',
              name: config.storeName,
            },
          },
        ],
      }}
    />
  )
}

export default LYSProductJsonLd
